import { useEffect, useState } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { PropertyDetail } from '../types/property'
import Header from '../components/Header'

export default function PropertyDetailPage() {
  const { listingKey } = useParams<{ listingKey: string }>()
  const navigate = useNavigate()
  const [property, setProperty] = useState<PropertyDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentImageIndex, setCurrentImageIndex] = useState(0)

  useEffect(() => {
    if (!listingKey) return

    const fetchProperty = async () => {
      setLoading(true)
      setError(null)

      try {
        const response = await fetch(`/.netlify/functions/property?listingKey=${listingKey}`)

        if (!response.ok) {
          throw new Error('Property not found')
        }

        const data: PropertyDetail = await response.json()
        setProperty(data)

        // Update Open Graph meta tags
        document.title = `${data.address} - $${data.price.toLocaleString()} | Coleman Colorado Collective`
      } catch (err: any) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    }

    fetchProperty()
  }, [listingKey])

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href)
    alert('Property link copied!')
  }

  const handleContact = () => {
    window.open(`mailto:${property?.listAgentEmail || 'info@colemancolorado.com'}?subject=Inquiry about ${property?.address}`, '_blank')
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading property...</p>
        </div>
      </div>
    )
  }

  if (error || !property) {
    return (
      <div className="flex items-center justify-center h-screen p-8">
        <div className="text-center">
          <svg className="w-16 h-16 text-red-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Property Not Found</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Link to="/homes" className="text-blue-600 hover:underline">
            Back to Search
          </Link>
        </div>
      </div>
    )
  }

  const images = property.media && property.media.length > 0
    ? property.media.map(m => m.url)
    : property.image
    ? [property.image]
    : []

  return (
    <div className="min-h-screen bg-white pt-[72px] md:pt-[88px]">
      {/* Site Header */}
      <Header />

      {/* Page Header */}
      <div className="sticky top-[72px] md:top-[88px] z-40 bg-white border-b border-gray-200 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-black">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Search</span>
          </button>
          <div className="flex gap-2">
            <button onClick={handleShare} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
              Share
            </button>
            <button onClick={handleContact} className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800">
              Contact Agent
            </button>
          </div>
        </div>
      </div>

      {/* Images Gallery */}
      <div className="relative h-96 bg-gray-200">
        {images.length > 0 ? (
          <>
            <img
              src={images[currentImageIndex]}
              alt={property.address}
              className="w-full h-full object-cover"
            />
            {images.length > 1 && (
              <>
                <button
                  onClick={() => setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length)}
                  className="absolute left-4 top-1/2 -translate-y-1/2 p-2 bg-white rounded-full shadow hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => setCurrentImageIndex(prev => (prev + 1) % images.length)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 p-2 bg-white rounded-full shadow hover:bg-gray-100"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`w-2 h-2 rounded-full ${idx === currentImageIndex ? 'bg-white' : 'bg-white/50'}`}
                    />
                  ))}
                </div>
              </>
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <svg className="w-24 h-24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Price & Address */}
            <div>
              <div className="text-4xl font-bold text-gray-900 mb-2">
                ${property.price.toLocaleString()}
              </div>
              <div className="text-xl text-gray-700">
                {property.address}
              </div>
              <div className="text-gray-600">
                {property.city}, {property.state} {property.zip}
              </div>
            </div>

            {/* Key Details */}
            <div className="flex flex-wrap gap-6 text-lg">
              <div>
                <span className="font-semibold">{property.beds}</span> Beds
              </div>
              <div>
                <span className="font-semibold">{property.baths}</span> Baths
              </div>
              <div>
                <span className="font-semibold">{property.sqft?.toLocaleString()}</span> sqft
              </div>
              {property.lotSize && (
                <div>
                  <span className="font-semibold">{property.lotSize}</span> acres
                </div>
              )}
            </div>

            {/* Description */}
            {property.description && (
              <div>
                <h3 className="text-xl font-bold mb-3">About This Property</h3>
                <p className="text-gray-700 whitespace-pre-wrap">{property.description}</p>
              </div>
            )}

            {/* Property Details */}
            <div>
              <h3 className="text-xl font-bold mb-3">Property Details</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Property Type</span>
                  <span className="font-medium">{property.propertyType}</span>
                </div>
                <div className="flex justify-between py-2 border-b border-gray-200">
                  <span className="text-gray-600">Status</span>
                  <span className="font-medium">{property.status}</span>
                </div>
                {property.yearBuilt && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Year Built</span>
                    <span className="font-medium">{property.yearBuilt}</span>
                  </div>
                )}
                {property.parking && (
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600">Parking</span>
                    <span className="font-medium">{property.parking} spaces</span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card */}
            <div className="bg-gray-50 rounded-lg p-6 space-y-4">
              <h3 className="font-bold text-lg">Contact Agent</h3>
              {property.listAgent && <p className="font-medium">{property.listAgent}</p>}
              {property.listOffice && <p className="text-sm text-gray-600">{property.listOffice}</p>}
              {property.listAgentPhone && (
                <p className="text-sm">
                  <a href={`tel:${property.listAgentPhone}`} className="text-blue-600 hover:underline">
                    {property.listAgentPhone}
                  </a>
                </p>
              )}
              <button
                onClick={handleContact}
                className="w-full px-4 py-3 bg-black text-white rounded-lg font-medium hover:bg-gray-800"
              >
                Request Info
              </button>
            </div>

            {/* Additional Info */}
            <div className="bg-gray-50 rounded-lg p-6 space-y-3">
              <h3 className="font-bold text-lg">Additional Info</h3>
              {property.mlsNumber && (
                <div className="text-sm">
                  <span className="text-gray-600">MLS #</span>
                  <span className="font-medium ml-2">{property.mlsNumber}</span>
                </div>
              )}
              {property.hoaFee && (
                <div className="text-sm">
                  <span className="text-gray-600">HOA Fee</span>
                  <span className="font-medium ml-2">${property.hoaFee}/{property.hoaFrequency || 'month'}</span>
                </div>
              )}
              {property.taxAnnualAmount && (
                <div className="text-sm">
                  <span className="text-gray-600">Property Tax</span>
                  <span className="font-medium ml-2">${property.taxAnnualAmount.toLocaleString()}/year</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
